// =============================================================================
// AuthorInfoPanel — krok "Autor info" (faza Ulepszanie zaplecza).
// Cheat-sheet do zarządzania kontami autora: czyta WŁĄCZONE profile social z aktywnego
// Writing Profile (author_same_as — to co idzie do schemy) i pozwala dopisać login/hasło/
// notatki per konto + notatki ogólne + biblioteka zdjęć autora. NIE publikowane na WP.
// =============================================================================

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  UserCircle, Images, Upload, Download, Trash2, Loader2, Save, Link2,
  ShieldCheck, ShieldOff, AlertTriangle, ExternalLink, Eye, EyeOff, Plus, X,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMapPersona } from "@/hooks/useMapPersona";
import { useAuthorPhotos } from "@/hooks/useAuthorPhotos";
import { AuthorPhotoGenerator } from "./AuthorPhotoGenerator";
import { matchAuthorPlatform } from "@/services/schema/socialPlatforms";
import type { ThematicMap } from "@/types/thematicMaps";
import type { PersonaSocialLink } from "@/types/uniquePages";

type AccountCreds = { login: string; password: string; notes: string; extra: string[] };

/** onProgress — sygnał do steppera, że krok właśnie się „zrobił" (zielona kropka bez przeładowania). */
export function AuthorInfoPanel({ map, onProgress }: { map: ThematicMap; onProgress?: () => void }) {
  const { toast } = useToast();
  const { persona, save, saving } = useMapPersona(map.id);
  const photos = useAuthorPhotos(map.id);

  const profileId = (map as any).active_profile_id as string | null;
  const [profileLoading, setProfileLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState(false);
  const [sameAs, setSameAs] = useState<string[]>([]);
  const [schemaEnabled, setSchemaEnabled] = useState(false);
  const [profileLabel, setProfileLabel] = useState("");

  const [fullName, setFullName] = useState("");
  const [service, setService] = useState("");
  const [notes, setNotes] = useState("");
  const [accounts, setAccounts] = useState<Record<string, AccountCreds>>({});
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});

  // Załaduj włączone profile social z aktywnego Writing Profile
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setProfileLoading(true);
      if (!profileId) { setHasProfile(false); setProfileLoading(false); return; }
      const { data } = await (supabase as any)
        .from("thematic_writing_profiles")
        .select("author_same_as, schema_enabled, public_author_label, profile_name, name")
        .eq("id", profileId)
        .maybeSingle();
      if (cancelled) return;
      setHasProfile(!!data);
      setSameAs(Array.isArray(data?.author_same_as) ? data.author_same_as.filter(Boolean) : []);
      setSchemaEnabled(!!data?.schema_enabled);
      setProfileLabel(data?.public_author_label || data?.profile_name || data?.name || "");
      setProfileLoading(false);
    })();
    return () => { cancelled = true; };
  }, [profileId]);

  // Persona → formularz (+ prefill imienia z profilu)
  useEffect(() => {
    setFullName(persona?.full_name ?? profileLabel ?? "");
    setService(persona?.service_label ?? ((map as any).name ?? ""));
    setNotes(persona?.notes ?? "");
    const acc: Record<string, AccountCreds> = {};
    (Array.isArray(persona?.social_links) ? persona!.social_links : []).forEach((l: PersonaSocialLink) => {
      if (l.url) acc[l.url] = { login: l.login ?? "", password: l.password ?? "", notes: l.notes ?? "", extra: Array.isArray(l.extra) ? l.extra : [] };
    });
    setAccounts(acc);
  }, [persona, profileLabel, map]);

  const emptyCreds = (): AccountCreds => ({ login: "", password: "", notes: "", extra: [] });
  const setAcc = (url: string, field: "login" | "password" | "notes", val: string) =>
    setAccounts((a) => ({ ...a, [url]: { ...emptyCreds(), ...a[url], [field]: val } }));
  const addExtra = (url: string) =>
    setAccounts((a) => ({ ...a, [url]: { ...emptyCreds(), ...a[url], extra: [...(a[url]?.extra ?? []), ""] } }));
  const setExtra = (url: string, idx: number, val: string) =>
    setAccounts((a) => {
      const cur = a[url] ?? emptyCreds();
      const extra = [...(cur.extra ?? [])];
      extra[idx] = val;
      return { ...a, [url]: { ...cur, extra } };
    });
  const removeExtra = (url: string, idx: number) =>
    setAccounts((a) => {
      const cur = a[url] ?? emptyCreds();
      return { ...a, [url]: { ...cur, extra: (cur.extra ?? []).filter((_, j) => j !== idx) } };
    });

  const handleSave = async () => {
    const social_links: PersonaSocialLink[] = sameAs.map((url) => {
      const p = matchAuthorPlatform(url);
      const a = accounts[url] || emptyCreds();
      return {
        platform: p?.displayName || "",
        url,
        login: a.login,
        password: a.password,
        notes: a.notes,
        extra: (a.extra ?? []).filter((v) => v.trim() !== ""),
      };
    });
    await save({ full_name: fullName, service_label: service, notes, social_links });
    onProgress?.();
    toast({ title: "Zapisano dane autora" });
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files || !files.length) return;
    const res = await photos.upload(files);
    onProgress?.();
    toast({
      title: `Wgrano ${res.ok} zdjęć`,
      description: res.failed ? `Błędy: ${res.errors.join("; ")}` : undefined,
      variant: res.failed ? "destructive" : undefined,
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="flex items-center gap-2 text-base font-semibold"><UserCircle className="h-4 w-4" />Autor — dane i konta</h3>
        <p className="text-sm text-muted-foreground">
          Konta social autora pochodzą z <span className="font-medium">Profilu</span> (włączone linki idące do schemy). Tutaj
          dopisujesz login / hasło / notatki do zakładania tych profili. Dane nie są publikowane.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Lewa: dane + konta social */}
        <div className="space-y-3">
          <div className="space-y-2">
            <Input placeholder="Imię i nazwisko" value={fullName} onChange={(e) => setFullName(e.target.value)} />
            <Input placeholder="Serwis (np. domena)" value={service} onChange={(e) => setService(e.target.value)} />
            <Textarea placeholder="Notatki ogólne (np. logowanie przez proxy, wskazówki…)" value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-[70px]" />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Konta social (z Profilu)</span>
            <Badge variant="outline" className={schemaEnabled ? "text-emerald-500 border-emerald-500/40" : "text-muted-foreground"}>
              {schemaEnabled ? <ShieldCheck className="mr-1 h-3 w-3" /> : <ShieldOff className="mr-1 h-3 w-3" />}
              schema {schemaEnabled ? "ON" : "OFF"}
            </Badge>
          </div>

          {profileLoading ? (
            <p className="text-sm text-muted-foreground">Ładowanie profilu…</p>
          ) : !hasProfile ? (
            <p className="flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-600 dark:text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5" />Brak aktywnego profilu — wybierz/utwórz go w kroku „Profil".
            </p>
          ) : sameAs.length === 0 ? (
            <p className="flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-600 dark:text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5" />Brak włączonych profili social w „Profil" — włącz je (toggle ON), a pojawią się tutaj.
            </p>
          ) : (
            <div className="space-y-2">
              {sameAs.map((url) => {
                const p = matchAuthorPlatform(url);
                const a = accounts[url] || emptyCreds();
                const show = !!showPassword[url];
                return (
                  <div key={url} className="space-y-1.5 rounded-md border border-border/60 p-2.5">
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                      {p?.displayName || "Profil"}
                      <a href={url} target="_blank" rel="noreferrer" className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
                        <span className="max-w-[220px] truncate">{url}</span><ExternalLink className="h-3 w-3" />
                      </a>
                    </div>

                    {/* login */}
                    <div className="flex items-center gap-2">
                      <span className="w-14 shrink-0 text-xs text-muted-foreground">login</span>
                      {/* anti-autofill: bez tego Chrome wstrzykuje zapisane dane logowania do PANELU */}
                      <Input
                        className="h-7 flex-1 text-xs"
                        placeholder="login / e-mail"
                        value={a.login}
                        onChange={(e) => setAcc(url, "login", e.target.value)}
                        autoComplete="off"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-bwignore="true"
                      />
                    </div>

                    {/* hasło z okiem */}
                    <div className="flex items-center gap-2">
                      <span className="w-14 shrink-0 text-xs text-muted-foreground">hasło</span>
                      <div className="relative flex-1">
                        <Input
                          type={show ? "text" : "password"}
                          className="h-7 pr-8 text-xs"
                          placeholder="hasło"
                          value={a.password}
                          onChange={(e) => setAcc(url, "password", e.target.value)}
                          /* "new-password" = jedyny sygnał którego Chrome słucha:
                             NIE podstawiaj zapisanego hasła do tej domeny */
                          autoComplete="new-password"
                          data-lpignore="true"
                          data-1p-ignore="true"
                          data-bwignore="true"
                        />
                        <button
                          type="button"
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          title={show ? "Ukryj hasło" : "Pokaż hasło"}
                          onClick={() => setShowPassword((s) => ({ ...s, [url]: !s[url] }))}
                        >
                          {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </div>

                    {/* dodatkowe info — główne */}
                    <div className="flex items-center gap-2">
                      <span className="w-14 shrink-0 text-xs text-muted-foreground">info</span>
                      <Input className="h-7 flex-1 text-xs" placeholder="np. recovery, 2FA, data założenia" value={a.notes} onChange={(e) => setAcc(url, "notes", e.target.value)} autoComplete="off" data-lpignore="true" data-1p-ignore="true" />
                    </div>

                    {/* dodatkowe pola info — dodawane i usuwane */}
                    {(a.extra ?? []).map((val, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="w-14 shrink-0 text-xs text-muted-foreground">info</span>
                        <Input className="h-7 flex-1 text-xs" placeholder="dodatkowe info" value={val} onChange={(e) => setExtra(url, idx, e.target.value)} autoComplete="off" data-lpignore="true" data-1p-ignore="true" />
                        <Button size="sm" variant="ghost" className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-destructive" title="Usuń pole" onClick={() => removeExtra(url, idx)}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}

                    <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => addExtra(url)}>
                      <Plus className="mr-1 h-3 w-3" />Dodaj pole
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}Zapisz dane autora
          </Button>
        </div>

        {/* Prawa: biblioteka zdjęć */}
        <div className="space-y-3">
          <AuthorPhotoGenerator
            map={map}
            hasPhotos={photos.photos.length > 0}
            savedPrompt={persona?.author_photo_prompt ?? null}
            onPromptChange={(prompt) => save({ author_photo_prompt: prompt })}
            onSaved={async () => { await photos.refresh(); onProgress?.(); }}
          />

          <h4 className="flex items-center gap-2 text-sm font-semibold"><Images className="h-4 w-4" />Biblioteka zdjęć autora</h4>
          <p className="text-xs text-muted-foreground">Wgraj kilka zdjęć autora do tej mapy — do zakładania profili social. Nie są publikowane na WP.</p>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border/60 px-3 py-1.5 text-sm hover:bg-muted/40">
            {photos.uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            Wgraj zdjęcia
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { void handleUpload(e.target.files); e.currentTarget.value = ""; }} />
          </label>

          {photos.loading ? (
            <p className="text-sm text-muted-foreground">Ładowanie…</p>
          ) : photos.photos.length === 0 ? (
            <p className="text-sm text-muted-foreground">Brak zdjęć.</p>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {photos.photos.map((ph) => (
                <div key={ph.path} className="group relative overflow-hidden rounded-md border border-border/60">
                  <img src={ph.url} alt={ph.name} className="aspect-square w-full object-cover" loading="lazy" />
                  <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 bg-black/60 p-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button size="sm" variant="ghost" className="h-6 flex-1 px-1 text-[10px] text-white hover:bg-white/20" onClick={() => photos.download(ph)}>
                      <Download className="mr-0.5 h-3 w-3" />Pobierz
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-white hover:bg-white/20" onClick={() => photos.remove(ph.path)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
